import { useEffect, useMemo, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { ALLERGENS, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Printer, QrCode, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

export function MatrixCell({ state }) {
  if (state === "presente") return <td className="border-b border-l px-1 py-2 text-center" style={{ background: "#FEE2E2", color: "#7F1D1D" }}><span className="text-xs font-bold">●</span><span className="sr-only">Presente</span></td>;
  if (state === "tracce") return <td className="border-b border-l px-1 py-2 text-center" style={{ background: "#FEF3C7", color: "#78350F" }}><span className="text-xs font-bold">◐</span><span className="sr-only">Tracce</span></td>;
  return <td className="border-b border-l px-1 py-2 text-center text-slate-300"><span className="text-xs">–</span><span className="sr-only">Assente</span></td>;
}

export function MatrixTable({ data, compact = false, mobileCards = false }) {
  if (mobileCards) {
    return (
      <div className="space-y-3 md:hidden" data-testid="matrix-mobile">
        {data.dishes.map((d) => (
          <div key={d.id} className="card p-4">
            <div className="flex items-start justify-between gap-2"><div><div className="font-semibold">{d.name}</div><div className="text-xs text-slate-500">{d.category}</div></div>{d.in_review && <span className="badge badge-amber">In revisione</span>}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.keys(d.allergens).length === 0 ? <span className="text-sm text-slate-500">Nessun allergene dichiarato</span>
                : Object.entries(d.allergens).map(([c, s]) => <span key={c} className={`badge ${s === "presente" ? "badge-red" : "badge-amber"}`}>{c} · {ALLERGENS.find((a) => a[0] === c)?.[1]}{s === "tracce" ? " (tracce)" : ""}</span>)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <table className={`w-full border-collapse bg-white text-sm ${compact ? "text-xs" : ""}`} data-testid="allergen-matrix-table">
      <thead>
        <tr className="bg-slate-50">
          <th className="sticky left-0 z-10 border-b bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Piatto</th>
          {data.allergens.map((a) => <th key={a.code} className="border-b border-l px-1 py-2 text-center text-[11px] font-semibold text-slate-700" title={a.name}><div>{a.code}</div><div className="mt-0.5 hidden text-[9px] font-normal leading-tight text-slate-500 lg:block">{a.name}</div></th>)}
        </tr>
      </thead>
      <tbody>
        {data.dishes.map((d) => (
          <tr key={d.id} className="hover:bg-slate-50/60" data-testid={`matrix-row-${d.id}`}>
            <td className="sticky left-0 z-10 border-b bg-white px-3 py-2 font-medium">{d.name}<span className="ml-2 text-xs font-normal text-slate-500">{d.category}</span>{d.in_review && <span className="badge badge-amber ml-2">In revisione</span>}</td>
            {data.allergens.map((a) => <MatrixCell key={a.code} state={d.allergens[a.code]} />)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function MatrixLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="matrix-legend">
      <span className="flex items-center gap-1.5"><span className="inline-block h-4 w-4 rounded" style={{ background: "#FEE2E2" }} /> ● Presente</span>
      <span className="flex items-center gap-1.5"><span className="inline-block h-4 w-4 rounded" style={{ background: "#FEF3C7" }} /> ◐ Possibili tracce</span>
      <span className="flex items-center gap-1.5"><span className="inline-block h-4 w-4 rounded border" /> – Assente</span>
      <span className="ml-auto">{ALLERGENS.map(([c, n]) => `${c} ${n}`).join(" · ")}</span>
    </div>
  );
}

export default function AllergenMatrixPage() {
  const { siteId } = useAuth();
  const { data, loading, error, reload } = useFetch(() => api.get("/allergen-matrix").then((r) => r.data), []);
  const publicUrl = `${window.location.origin}/public/allergeni/${siteId}`;
  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/qr?url=${encodeURIComponent(publicUrl)}`;

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  return (
    <div className="fade-up" data-testid="allergen-matrix-page">
      <div className="no-print">
        <PageHeader title="Matrice allergeni" subtitle={`Menu attivo · ${data.dishes.length} piatti · Ultimo aggiornamento ${fmtDateTime(data.updated_at)}`}
          actions={<>
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" data-testid="qr-button"><QrCode className="h-4 w-4" /> QR per i clienti</Button></DialogTrigger>
              <DialogContent data-testid="qr-dialog">
                <DialogHeader><DialogTitle>Pagina pubblica allergeni</DialogTitle><DialogDescription>Stampa il QR e mettilo in sala: apre l'elenco piatti e allergeni senza login, senza prezzi né foto.</DialogDescription></DialogHeader>
                <div className="flex flex-col items-center gap-3"><img src={qrUrl} alt="QR code pagina allergeni" className="h-56 w-56 rounded-md border" /><a href={publicUrl} target="_blank" rel="noreferrer" className="break-all text-sm text-primary underline" data-testid="public-link">{publicUrl}</a></div>
              </DialogContent>
            </Dialog>
            <Button onClick={() => window.print()} data-testid="print-button"><Printer className="h-4 w-4" /> Stampa A4 / PDF</Button>
          </>} />
      </div>
      <div className="hidden print:block">
        <h1 className="text-xl font-bold">{data.site.name} · Tabella allergeni</h1>
        <p className="mb-3 text-xs text-slate-600">Reg. UE 1169/2011 Allegato II · Aggiornata il {fmtDateTime(data.updated_at)}</p>
      </div>
      {data.dishes.length === 0 ? <EmptyState icon={ShieldAlert} title="Nessun piatto nel menu attivo" description="La matrice mostra le ricette approvate (escluse le Basi). Approva una ricetta per vederla qui." action={<Button asChild variant="outline"><Link to="/ricette">Vai al ricettario</Link></Button>} /> : (
        <>
          <div className="card hidden overflow-x-auto md:block print:block print:border-0 print:shadow-none"><MatrixTable data={data} /></div>
          <div className="print:hidden"><MatrixTable data={data} mobileCards /></div>
          <div className="mt-3"><MatrixLegend /></div>
        </>
      )}
    </div>
  );
}
